import type { TextStyle } from '../data/miroFonts'

/**
 * Mood board layouts (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * One engine for every place a mood board is made: the website's Mood board
 * page now, the Miro panel and the canvas later. Pure arithmetic -- no DOM, no
 * Miro -- so the three can never disagree about where a picture goes.
 *
 * Everything is laid out on a 1920 x 1080 sheet, 16:9, the shape of the frame
 * it becomes on the board (3840 x 2160 there, everything doubled).
 *
 * What makes it look considered rather than dumped (the discussion of
 * 2026-10-12): one picture leads and the rest support it; the gutters are
 * narrow and all the same; every edge lines up; the margin is generous; the
 * words are few and small. Each layout is a different rhythm of that.
 *
 * Pictures FILL their places and are cropped to them, from the middle (a
 * little above it vertically, where a product's label usually is not).
 * Every layout is chosen to keep that crop small.
 */

export const SHEET_W = 1920
export const SHEET_H = 1080

export type LayoutKind = 'editorial' | 'gallery' | 'grid' | 'collage' | 'half' | 'centre' | 'hero' | 'strip'

/**
 * Four more since 2026-10-15 (Huanrui: "one picture taking only the right
 * half", and a few others to choose from). The first picture on the sheet
 * takes the large place in every one; which picture that is, is settled by
 * dragging pictures between frames.
 */
export const LAYOUTS: { kind: LayoutKind; label: string; hint: string }[] = [
  { kind: 'editorial', label: 'Editorial', hint: 'One tall picture on the left, the rest beside it' },
  { kind: 'half', label: 'Half', hint: 'The left half left empty; every picture in the right half' },
  { kind: 'gallery', label: 'Gallery', hint: 'A row of large pictures over a row of details' },
  { kind: 'hero', label: 'Hero', hint: 'One wide picture across the top, a row beneath it' },
  { kind: 'centre', label: 'Centre', hint: 'One picture in the middle, the rest either side' },
  { kind: 'strip', label: 'Strip', hint: 'Every picture in one row, full height, all the same width' },
  { kind: 'collage', label: 'Collage', hint: 'Columns of different heights, edges still aligned' },
  { kind: 'grid', label: 'Grid', hint: 'Even rows, every picture the same weight' },
]

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** Which part of the picture shows, as fractions of its width and height. */
export interface Crop {
  x: number
  y: number
  w: number
  h: number
}

export interface Slot extends Rect {
  /** Index into the board's pictures. */
  pic: number
  crop: Crop
}

export interface BoardText extends Rect {
  id: 'title' | 'keywords'
  text: string
  style: TextStyle
}

export interface Swatch extends Rect {
  color: string
}

export interface Board {
  title: string
  /** #rrggbb */
  background: string
  slots: Slot[]
  texts: BoardText[]
  swatches: Swatch[]
}

/**
 * The margin round the sheet, and the gutter between pictures. MARGIN is
 * also where the title strip and the colours always sit; the pictures' own
 * margin can be brought in from it to the edge (2026-10-23, `margin`).
 */
export const MARGIN = 56
export const GAP = 14
/** The strip at the foot for the title, the words and the colours. */
export const FOOT = 52
const FOOT_GAP = 22
/** How tall the strip may be made (2026-10-16). */
export const FOOT_MIN = 36
export const FOOT_MAX = 160

/** Half: how much of the width the pictures may take, and the usual half. */
export const HALF_MIN = 0.25
export const HALF_MAX = 0.9

/** The crop that fills a place of this shape from a picture of that one. */
export function coverCrop(picRatio: number, slotW: number, slotH: number): Crop {
  const slot = slotW / slotH
  const pic = Number.isFinite(picRatio) && picRatio > 0 ? picRatio : 1
  if (pic > slot) {
    const w = slot / pic
    return { x: (1 - w) / 2, y: 0, w, h: 1 }
  }
  const h = pic / slot
  // A little above the middle: a pack shot's top is more telling than its base.
  return { x: 0, y: (1 - h) * 0.42, w: 1, h }
}

/** Every way of cutting n things, in order, into k non-empty runs. */
function splits(n: number, k: number): number[][] {
  const out: number[][] = []
  const walk = (left: number, parts: number, acc: number[]) => {
    if (parts === 1) {
      out.push([...acc, left])
      return
    }
    for (let take = 1; take <= left - (parts - 1); take += 1) walk(left - take, parts - 1, [...acc, take])
  }
  if (k >= 1 && k <= n) walk(n, k, [])
  return out
}

/**
 * Fill a rectangle edge to edge with rows of pictures, in order.
 *
 * Each row is as tall as it needs to be for its pictures to fill the width at
 * their own shapes; then all the rows are stretched together to fill the
 * height, which crops every picture by the same small amount. Of all the ways
 * to break the pictures into rows, the one needing the least stretch wins,
 * with a slight preference for fewer rows (bigger pictures).
 */
export function fillRows(rect: Rect, ratios: number[], maxRows = 4): Rect[] {
  const n = ratios.length
  if (n === 0) return []
  let best: { rows: number[]; score: number } | null = null
  for (let k = 1; k <= Math.min(n, maxRows); k += 1) {
    for (const rows of splits(n, k)) {
      let at = 0
      const heights: number[] = []
      for (const count of rows) {
        const sum = ratios.slice(at, at + count).reduce((a, b) => a + b, 0)
        heights.push((rect.w - GAP * (count - 1)) / sum)
        at += count
      }
      const natural = heights.reduce((a, b) => a + b, 0)
      const stretch = (rect.h - GAP * (k - 1)) / natural
      // Rows of very different heights read as a mistake, not a rhythm.
      const mean = natural / k
      const uneven = heights.reduce((sum, h) => sum + Math.abs(Math.log(h / mean)), 0) / k
      const score = Math.abs(Math.log(stretch)) + 0.6 * uneven + 0.04 * k
      if (!best || score < best.score) best = { rows, score }
    }
  }
  const rows = best!.rows
  const naturals: number[] = []
  let at = 0
  for (const count of rows) {
    const sum = ratios.slice(at, at + count).reduce((a, b) => a + b, 0)
    naturals.push((rect.w - GAP * (count - 1)) / sum)
    at += count
  }
  const stretch = (rect.h - GAP * (rows.length - 1)) / naturals.reduce((a, b) => a + b, 0)
  const out: Rect[] = []
  let y = rect.y
  at = 0
  rows.forEach((count, r) => {
    const natural = naturals[r]
    const height = natural * stretch
    let x = rect.x
    for (let i = 0; i < count; i += 1) {
      // The last in a row takes whatever is left, so rounding never leaves a hairline.
      const width = i === count - 1 ? rect.x + rect.w - x : ratios[at + i] * natural
      out.push({ x, y, w: width, h: height })
      x += width + GAP
    }
    y += height + GAP
    at += count
  })
  return out
}

/** How far a set of places is from its pictures' own shapes: 0 is no crop at all. */
function cropCost(rects: Rect[], ratios: number[]): number {
  if (rects.length === 0) return 0
  let sum = 0
  rects.forEach((r, i) => {
    sum += Math.abs(Math.log(r.w / r.h / ratios[i]))
  })
  return sum / rects.length
}

/** Columns of different heights, each stretched to the full height. */
function fillColumns(rect: Rect, ratios: number[], columns: number): Rect[] {
  const cols = Math.max(1, Math.min(columns, ratios.length))
  const colW = (rect.w - GAP * (cols - 1)) / cols
  const lists: number[][] = Array.from({ length: cols }, () => [])
  const heights = new Array(cols).fill(0)
  ratios.forEach((ratio, i) => {
    // The lead picture opens the first column; the rest go to the shortest.
    const c = i === 0 ? 0 : heights.indexOf(Math.min(...heights))
    lists[c].push(i)
    heights[c] += colW / ratio + GAP
  })
  const out: Rect[] = new Array(ratios.length)
  lists.forEach((list, c) => {
    if (list.length === 0) return
    const natural = list.map((i) => colW / ratios[i])
    const scale = (rect.h - GAP * (list.length - 1)) / natural.reduce((a, b) => a + b, 0)
    let y = rect.y
    list.forEach((i, j) => {
      const h = j === list.length - 1 ? rect.y + rect.h - y : natural[j] * scale
      out[i] = { x: rect.x + c * (colW + GAP), y, w: colW, h }
      y += h + GAP
    })
  })
  return out
}

/** Even rows of equal places. */
function fillGrid(rect: Rect, n: number, ratios: number[]): Rect[] {
  // Places the shape of the typical picture, rows filled evenly.
  const sorted = [...ratios].sort((a, b) => a - b)
  const typical = sorted[Math.floor(sorted.length / 2)] || 1
  let bestRows = 1
  let bestScore = Infinity
  for (let rows = 1; rows <= Math.min(n, 4); rows += 1) {
    const perRow = Math.ceil(n / rows)
    const cellW = (rect.w - GAP * (perRow - 1)) / perRow
    const cellH = (rect.h - GAP * (rows - 1)) / rows
    const score = Math.abs(Math.log(cellW / cellH / typical))
    if (score < bestScore) {
      bestScore = score
      bestRows = rows
    }
  }
  const out: Rect[] = []
  const rowH = (rect.h - GAP * (bestRows - 1)) / bestRows
  let left = n
  for (let r = 0; r < bestRows; r += 1) {
    const count = Math.ceil(left / (bestRows - r))
    const cellW = (rect.w - GAP * (count - 1)) / count
    for (let i = 0; i < count; i += 1) {
      out.push({ x: rect.x + i * (cellW + GAP), y: rect.y + r * (rowH + GAP), w: cellW, h: rowH })
    }
    left -= count
  }
  return out
}

export interface LayoutInput {
  /** Width over height of each picture, the lead first. */
  ratios: number[]
  /** The foot strip: title, words, colours. Off gives the pictures the whole sheet. */
  foot: boolean
  /** Where the strip goes, and how tall it is (2026-10-16). Bottom and FOOT unless said. */
  footAt?: 'top' | 'bottom'
  footH?: number
  /** Half: the share of the width the pictures take, from the right (2026-10-16). About a half unless said. */
  half?: number
  /**
   * How far the pictures keep from the sheet's edges, 0 to MARGIN (Huanrui,
   * 2026-10-23). MARGIN unless said. The title strip does not move with it:
   * on the strip's side the pictures stop where they always have, short of
   * the strip.
   */
  margin?: number
}

/** Where every picture goes, in the order given. */
export function placePictures(kind: LayoutKind, input: LayoutInput): Rect[] {
  const { ratios } = input
  const n = ratios.length
  const strip = input.foot ? (input.footH ?? FOOT) + FOOT_GAP : 0
  const m = Math.min(MARGIN, Math.max(0, input.margin ?? MARGIN))
  // The strip's side keeps the old line (MARGIN + strip); the others take the margin.
  const top = input.foot && input.footAt === 'top' ? MARGIN + strip : m
  const bottom = input.foot && input.footAt !== 'top' ? SHEET_H - MARGIN - strip : SHEET_H - m
  const area: Rect = { x: m, y: top, w: SHEET_W - m * 2, h: bottom - top }
  if (n === 0) return []
  // Half (Huanrui, 2026-10-15): the left half is left empty, on purpose; the
  // pictures, however many, share the right half.
  if (kind === 'half') {
    const share = Math.min(HALF_MAX, Math.max(HALF_MIN, input.half ?? 0.5))
    const half = Math.round(input.half === undefined ? (area.w - GAP) / 2 : area.w * share)
    const right: Rect = { x: area.x + area.w - half, y: area.y, w: half, h: area.h }
    if (n === 1) {
      const r = ratios[0]
      const w = Math.min(right.w, right.h * r)
      const h = w / r
      return [{ x: right.x + (right.w - w) / 2, y: right.y + (right.h - h) / 2, w, h }]
    }
    return fillRows(right, ratios, 4)
  }
  if (n === 1) {
    // One picture is shown whole, centred, rather than cut to the sheet.
    const r = ratios[0]
    const w = Math.min(area.w, area.h * r)
    const h = w / r
    return [{ x: area.x + (area.w - w) / 2, y: area.y + (area.h - h) / 2, w, h }]
  }

  switch (kind) {
    case 'editorial': {
      // The lead takes a tall column on the left, between about two fifths
      // and three fifths of the width: whichever crops everything least.
      let best: Rect[] = []
      let bestCost = Infinity
      for (let share = 0.38; share <= 0.6001; share += 0.02) {
        const leadW = Math.round(area.w * share)
        const rest: Rect = { x: area.x + leadW + GAP, y: area.y, w: area.w - leadW - GAP, h: area.h }
        const rects = [{ x: area.x, y: area.y, w: leadW, h: area.h }, ...fillRows(rest, ratios.slice(1), 3)]
        // The lead's own crop counts double: it is what the eye lands on.
        const cost = cropCost(rects, ratios) + Math.abs(Math.log(leadW / area.h / ratios[0])) / n
        if (cost < bestCost) {
          bestCost = cost
          best = rects
        }
      }
      return best
    }
    case 'gallery': {
      // Large pictures above, the details in a lower band.
      const top = n >= 6 ? 3 : 2
      // A lower band needs a few pictures to be a band; with fewer, two even rows.
      if (n - top < 3) return fillRows(area, ratios, 2)
      const topH = Math.round(area.h * 0.62)
      // The lead is the widest of the top row.
      const leadW = Math.round(area.w * (top === 3 ? 0.42 : 0.56))
      const upper = [
        { x: area.x, y: area.y, w: leadW, h: topH },
        ...fillRows({ x: area.x + leadW + GAP, y: area.y, w: area.w - leadW - GAP, h: topH }, ratios.slice(1, top), 1),
      ]
      const lower = fillRows(
        { x: area.x, y: area.y + topH + GAP, w: area.w, h: area.h - topH - GAP },
        ratios.slice(top),
        n - top > 6 ? 2 : 1,
      )
      return [...upper, ...lower]
    }
    case 'collage': {
      // As many columns as crop the pictures least, two to five.
      let best: Rect[] = []
      let bestCost = Infinity
      for (let columns = 2; columns <= Math.min(5, n); columns += 1) {
        const rects = fillColumns(area, ratios, columns)
        const cost = cropCost(rects, ratios) + 0.02 * columns
        if (cost < bestCost) {
          bestCost = cost
          best = rects
        }
      }
      return best
    }
    case 'centre': {
      // Two pictures have no two sides: the second takes the left.
      const rest = ratios.slice(1)
      const midW = Math.round(area.w * (rest.length < 2 ? 0.5 : 0.4))
      const sides = rest.length < 2 ? 1 : 2
      const sideW = (area.w - midW - GAP * sides) / sides
      const leftCount = Math.ceil(rest.length / sides)
      const left = fillRows({ x: area.x, y: area.y, w: sideW, h: area.h }, rest.slice(0, leftCount), 4)
      const mid: Rect = { x: area.x + sideW + GAP, y: area.y, w: midW, h: area.h }
      const right =
        sides === 2 ? fillRows({ x: mid.x + midW + GAP, y: area.y, w: sideW, h: area.h }, rest.slice(leftCount), 4) : []
      return [mid, ...left, ...right]
    }
    case 'hero': {
      // The first picture across the top; the rest in a band beneath.
      const topH = Math.round(area.h * 0.62)
      const top: Rect = { x: area.x, y: area.y, w: area.w, h: topH }
      const lower = fillRows(
        { x: area.x, y: area.y + topH + GAP, w: area.w, h: area.h - topH - GAP },
        ratios.slice(1),
        n - 1 > 6 ? 2 : 1,
      )
      return [top, ...lower]
    }
    case 'strip': {
      // Equal columns (Huanrui, 2026-10-23): every picture the same width,
      // cut to it, rather than each as wide as its own shape makes it.
      const w = (area.w - GAP * (n - 1)) / n
      return ratios.map((_, i) => ({ x: area.x + i * (w + GAP), y: area.y, w, h: area.h }))
    }
    case 'grid':
    default:
      return fillGrid(area, n, ratios)
  }
}

/**
 * The line crediting Unsplash and Pexels photographers, in the bottom margin
 * under everything else: small, but there, as both libraries ask.
 */
export function creditLine(): Rect {
  return { x: MARGIN, y: SHEET_H - MARGIN + 14, w: SHEET_W - MARGIN * 2, h: 24 }
}

/** The foot strip's three parts, for the boards that show it: at the foot, or across the top. */
export function footParts(
  swatchCount: number,
  at: 'top' | 'bottom' = 'bottom',
  height = FOOT,
): { title: Rect; keywords: Rect; swatches: Rect[] } {
  const tall = Math.min(FOOT_MAX, Math.max(FOOT_MIN, height))
  const y = at === 'top' ? MARGIN : SHEET_H - MARGIN - tall
  // The colour squares grow with the strip, a little.
  const size = Math.round(Math.min(tall, 34 * Math.sqrt(tall / 52)))
  const swatchGap = 8
  const swatches: Rect[] = []
  const right = SHEET_W - MARGIN
  for (let i = 0; i < swatchCount; i += 1) {
    const x = right - (swatchCount - i) * size - (swatchCount - i - 1) * swatchGap
    swatches.push({ x, y: y + (tall - size) / 2, w: size, h: size })
  }
  const swatchesW = swatchCount > 0 ? swatchCount * size + (swatchCount - 1) * swatchGap + 36 : 0
  return {
    title: { x: MARGIN, y, w: 760, h: tall },
    keywords: { x: MARGIN + 780, y, w: SHEET_W - MARGIN * 2 - 780 - swatchesW, h: tall },
    swatches,
  }
}
